import { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';
import { Edit3, AlertTriangle, RefreshCw, MapPin, X as XIcon } from 'lucide-react-native';

const log = createLogger('GYM_EDIT');

type EditType = 'rename' | 'rebrand' | 'address_change' | 'closed' | 'other';

interface GymEditFormProps {
  gym: { id: string; name: string; address?: string; city?: string; state?: string };
  onSubmitted: () => void;
  onCancel: () => void;
}

const EDIT_TYPES: { value: EditType; label: string; icon: typeof Edit3; desc: string }[] = [
  { value: 'rebrand', label: 'Rebranded', icon: RefreshCw, desc: 'Gym changed its name/brand' },
  { value: 'rename', label: 'Name is Wrong', icon: Edit3, desc: 'Current name is incorrect' },
  { value: 'address_change', label: 'Moved Location', icon: MapPin, desc: 'Gym moved to a new address' },
  { value: 'closed', label: 'Permanently Closed', icon: XIcon, desc: 'This gym no longer exists' },
  { value: 'other', label: 'Other Issue', icon: AlertTriangle, desc: 'Something else is wrong' },
];

export default function GymEditForm({ gym, onSubmitted, onCancel }: GymEditFormProps) {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const [editType, setEditType] = useState<EditType | null>(null);
  const [formData, setFormData] = useState({
    suggestedName: '',
    suggestedAddress: '',
    suggestedCity: '',
    suggestedState: '',
    reason: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!editType) {
      Alert.alert('Select Type', 'Choose what changed about this gym.');
      return;
    }
    if (editType !== 'closed' && !formData.reason.trim() && !formData.suggestedName.trim()) {
      Alert.alert('More Info Needed', 'Please provide the corrected info or a description of the change.');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.from('gym_edits').insert({
        gym_id: gym.id,
        edit_type: editType,
        suggested_name: formData.suggestedName || null,
        suggested_address: formData.suggestedAddress || null,
        suggested_city: formData.suggestedCity || null,
        suggested_state: formData.suggestedState || null,
        reason: formData.reason || (editType === 'closed' ? 'Permanently closed' : null),
        submitted_by: profile!.id,
      });

      if (error) throw error;

      Alert.alert(
        'Thanks!',
        "We'll review your suggestion and update the listing.",
        [{ text: 'OK', onPress: onSubmitted }],
      );
    } catch (err) {
      log.error('Error submitting gym edit:', err);
      Alert.alert('Oops', "Couldn't submit. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const styles = StyleSheet.create({
    container: { flex: 1 },
    scrollContent: { padding: 20 },
    header: { marginBottom: 24 },
    title: { fontSize: 22, fontWeight: '700', marginBottom: 4 },
    gymName: { fontSize: 16, marginBottom: 12 },
    description: { fontSize: 15, lineHeight: 22 },
    typeList: { gap: 10, marginBottom: 24 },
    typeCard: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      padding: 14,
      borderRadius: 12,
      borderWidth: 2,
      gap: 12,
    },
    typeLabel: { fontSize: 16, fontWeight: '600' },
    typeDesc: { fontSize: 13 },
    inputGroup: { gap: 8, marginBottom: 16 },
    label: { fontSize: 15, fontWeight: '600' },
    input: { borderWidth: 1, borderRadius: 12, padding: 14, fontSize: 16 },
    textArea: { minHeight: 80, textAlignVertical: 'top' as const },
    row: { flexDirection: 'row' as const, gap: 12 },
    flex1: { flex: 1 },
    buttonContainer: { flexDirection: 'row' as const, gap: 12, marginTop: 20 },
    button: {
      flex: 1, borderRadius: 12, padding: 16,
      alignItems: 'center' as const, justifyContent: 'center' as const,
    },
    buttonText: { fontSize: 16, fontWeight: '700' },
  });

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <ScrollView style={styles.scrollContent} showsVerticalScrollIndicator={false}>
        <View style={styles.header}>
          <Text style={[styles.title, { color: theme.colors.text }]}>
            Suggest an Edit
          </Text>
          <Text style={[styles.gymName, { color: theme.colors.accent }]}>
            {gym.name}
          </Text>
          <Text style={[styles.description, { color: theme.colors.textSecondary }]}>
            Something wrong or outdated? Let us know and we'll fix it.
          </Text>
        </View>

        {/* Edit type picker */}
        <View style={styles.typeList}>
          {EDIT_TYPES.map((t) => {
            const selected = editType === t.value;
            const Icon = t.icon;
            return (
              <TouchableOpacity
                key={t.value}
                style={[
                  styles.typeCard,
                  {
                    backgroundColor: selected ? theme.colors.accent + '15' : theme.colors.surface,
                    borderColor: selected ? theme.colors.accent : theme.colors.border,
                  },
                ]}
                onPress={() => setEditType(t.value)}
              >
                <Icon size={20} color={selected ? theme.colors.accent : theme.colors.textSecondary} />
                <View style={styles.flex1}>
                  <Text style={[styles.typeLabel, { color: theme.colors.text }]}>{t.label}</Text>
                  <Text style={[styles.typeDesc, { color: theme.colors.textSecondary }]}>{t.desc}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Conditional fields based on type */}
        {editType && editType !== 'closed' && (
          <>
            {(editType === 'rename' || editType === 'rebrand') && (
              <View style={styles.inputGroup}>
                <Text style={[styles.label, { color: theme.colors.text }]}>
                  {editType === 'rebrand' ? 'New Name' : 'Correct Name'}
                </Text>
                <TextInput
                  style={[styles.input, {
                    color: theme.colors.text,
                    backgroundColor: theme.colors.surface,
                    borderColor: theme.colors.border,
                  }]}
                  value={formData.suggestedName}
                  onChangeText={(v) => setFormData(p => ({ ...p, suggestedName: v }))}
                  placeholder={`What's the ${editType === 'rebrand' ? 'new' : 'correct'} name?`}
                  placeholderTextColor={theme.colors.textSecondary}
                />
              </View>
            )}

            {editType === 'address_change' && (
              <>
                <View style={styles.inputGroup}>
                  <Text style={[styles.label, { color: theme.colors.text }]}>New Address</Text>
                  <TextInput
                    style={[styles.input, {
                      color: theme.colors.text,
                      backgroundColor: theme.colors.surface,
                      borderColor: theme.colors.border,
                    }]}
                    value={formData.suggestedAddress}
                    onChangeText={(v) => setFormData(p => ({ ...p, suggestedAddress: v }))}
                    placeholder="New street address"
                    placeholderTextColor={theme.colors.textSecondary}
                  />
                </View>
                <View style={styles.row}>
                  <View style={[styles.inputGroup, styles.flex1]}>
                    <Text style={[styles.label, { color: theme.colors.text }]}>City</Text>
                    <TextInput
                      style={[styles.input, {
                        color: theme.colors.text,
                        backgroundColor: theme.colors.surface,
                        borderColor: theme.colors.border,
                      }]}
                      value={formData.suggestedCity}
                      onChangeText={(v) => setFormData(p => ({ ...p, suggestedCity: v }))}
                      placeholder="City"
                      placeholderTextColor={theme.colors.textSecondary}
                    />
                  </View>
                  <View style={[styles.inputGroup, { width: 80 }]}>
                    <Text style={[styles.label, { color: theme.colors.text }]}>State</Text>
                    <TextInput
                      style={[styles.input, {
                        color: theme.colors.text,
                        backgroundColor: theme.colors.surface,
                        borderColor: theme.colors.border,
                      }]}
                      value={formData.suggestedState}
                      onChangeText={(v) => setFormData(p => ({ ...p, suggestedState: v.toUpperCase() }))}
                      placeholder="ST"
                      placeholderTextColor={theme.colors.textSecondary}
                      maxLength={2}
                      autoCapitalize="characters"
                    />
                  </View>
                </View>
              </>
            )}

            <View style={styles.inputGroup}>
              <Text style={[styles.label, { color: theme.colors.text }]}>
                {editType === 'other' ? 'What needs to change?' : 'Additional details (optional)'}
              </Text>
              <TextInput
                style={[styles.input, styles.textArea, {
                  color: theme.colors.text,
                  backgroundColor: theme.colors.surface,
                  borderColor: theme.colors.border,
                }]}
                value={formData.reason}
                onChangeText={(v) => setFormData(p => ({ ...p, reason: v }))}
                placeholder="Any extra context..."
                placeholderTextColor={theme.colors.textSecondary}
                multiline
              />
            </View>
          </>
        )}

        {editType === 'closed' && (
          <View style={styles.inputGroup}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              Any details? (optional)
            </Text>
            <TextInput
              style={[styles.input, {
                color: theme.colors.text,
                backgroundColor: theme.colors.surface,
                borderColor: theme.colors.border,
              }]}
              value={formData.reason}
              onChangeText={(v) => setFormData(p => ({ ...p, reason: v }))}
              placeholder="e.g. Closed last month, building demolished"
              placeholderTextColor={theme.colors.textSecondary}
            />
          </View>
        )}

        <View style={styles.buttonContainer}>
          <TouchableOpacity
            style={[styles.button, { borderWidth: 2, borderColor: theme.colors.border, backgroundColor: theme.colors.surface }]}
            onPress={onCancel}
            disabled={loading}
          >
            <Text style={[styles.buttonText, { color: theme.colors.text }]}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.button, { backgroundColor: '#3b82f6' }]}
            onPress={handleSubmit}
            disabled={loading || !editType}
          >
            {loading ? (
              <ActivityIndicator size="small" color="white" />
            ) : (
              <Text style={[styles.buttonText, { color: 'white' }]}>Submit</Text>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
}
